import { createEffect, untrack } from 'solid-js';
import { PlaybackHeartbeatController } from '@dtvgo/player-utils';

import { middleware } from '~/services/middleware';
import { deviceId } from '~/store/deviceId';
import { Asset, getHeartbeatContent, Schedule } from '~/utils/metadata';
import { CreateGetStreamedTime } from '~/utils/types';

import { isLearnActionV4Active, useLearnActionClick } from '../../utils/env';
import { createAssetChangeEffect } from './createAssetChangeEffect';

export function createHeartbeatController(
  getConfig: () =>
    | {
        timeInSeconds: number;
        useStreamingV4: boolean;
        useStopV4: boolean;
      }
    | undefined,
  {
    streamedContentTime,
    streamedTime,
    progress,
    duration,
    userOrigin,
    actionOrigin,
    isLive,
    asset,
  }: {
    streamedContentTime: CreateGetStreamedTime;
    streamedTime: CreateGetStreamedTime;
    progress: () => number;
    duration: () => number;
    userOrigin: () => {
      playSource: string;
      msoProvider: string;
      userLogin: string;
    };
    actionOrigin: () => string;
    isLive: () => boolean;
    asset: () => Asset | Schedule | undefined;
  },
) {
  let heartbeatController: PlaybackHeartbeatController | undefined;
  const assetId = () => asset()?.vrioAssetId;

  createEffect(() => {
    if (heartbeatController) return;

    const config = getConfig();

    if (!config) return;

    heartbeatController = new PlaybackHeartbeatController({
      backendService: middleware,
      heartbeatTimeInSeconds: config.timeInSeconds,
      deviceId: deviceId(),
      useStreamingV4: config.useStreamingV4,
      useStopV4: isLearnActionV4Active,
      getStreamedTime: streamedTime.getElapsedSeconds,
      getStreamedContentTime: streamedContentTime.getElapsedSeconds,
    });
  });

  createEffect(() => {
    if (heartbeatController) {
      heartbeatController.updateDuration(duration());
    }
  });

  createEffect(() => {
    if (heartbeatController) {
      heartbeatController.updateProgress(progress());
    }
  });

  createAssetChangeEffect(assetId, () => {
    if (heartbeatController) {
      const currentAsset = untrack(asset);

      heartbeatController.setContent({
        isLive: untrack(isLive),
        ...(currentAsset && getHeartbeatContent(currentAsset)),
      });

      if (useLearnActionClick) {
        heartbeatController.updateActionOrigin(actionOrigin());
        heartbeatController.updateUserOrigin(userOrigin());
      }
    }
  });

  return {
    startHeartbeat: (
      params: Parameters<PlaybackHeartbeatController['start']>[0],
    ) => {
      if (heartbeatController) {
        heartbeatController.start(params).catch((startHeartbeatError) => {
          console.error(
            '[App>startHeartbeat] heartbeat start failed',
            startHeartbeatError,
          );
        });
      }
    },
    stopHeartbeat: () => {
      if (heartbeatController) {
        heartbeatController.stop().catch((stopHeartbeatError) => {
          console.error(
            '[App>stopHeartbeat] heartbeat stop failed',
            stopHeartbeatError,
          );
        });
      }
    },
    programCompletion: () => {
      if (heartbeatController) {
        heartbeatController
          .programCompletion()
          .catch((programCompletionError) => {
            console.error(
              '[App>programCompletion] heartbeat programCompletion failed',
              programCompletionError,
            );
          });
      }
    },
    updateProgress: (newProgress: number) => {
      if (heartbeatController) {
        heartbeatController.updateProgress(newProgress);
      }
    },
  };
}
